import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes } from 'lexical';
import { Image as ImageIcon } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fieldClasses, primaryButtonClasses } from '../../ui/styles';
import { ToolbarMenu } from '../../ui/ToolbarMenu';
import { $createImageNode } from './ImageNode';
import { useImageHandlers } from './useImageHandlers';

/**
 * Insert an image. Upload goes through `useImageHandlers` (`features.images` or `ImageUploadProvider`).
 * Without an upload handler only the "by URL" path is offered.
 */
export const ImageControl = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation('common');
  const { upload } = useImageHandlers();

  return (
    <ToolbarMenu icon={ImageIcon} caret="none" disabled={disabled} label={t('editor.toolbar.insert-image')}>
      {(close) => <ImageForm upload={upload} close={close} />}
    </ToolbarMenu>
  );
};

type UploadHandler = (blob: Blob) => Promise<{ url: string; fileId: string }>;

const ImageForm = ({ upload, close }: { upload?: UploadHandler; close: () => void }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const insert = useCallback(
    (src: string, fileId?: string) => {
      editor.update(() => {
        $insertNodes([$createImageNode({ src, fileId })]);
      });
    },
    [editor],
  );

  const onFile = async (file: File | undefined) => {
    if (!file || !upload) return;
    setUploading(true);
    try {
      const { url: uploadedUrl, fileId } = await upload(file);
      insert(uploadedUrl, fileId);
      close();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex w-64 flex-col gap-2 p-3">
      {upload && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button type="button" className={primaryButtonClasses} disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? t('editor.toolbar.uploading') : t('editor.toolbar.upload-image')}
          </button>
          <span className="text-pbk-12 text-text-2 text-center">{t('editor.toolbar.or')}</span>
        </>
      )}
      <label className="text-pbk-12 text-text-2" htmlFor="lexical-image-url">
        {t('editor.toolbar.image-url')}
      </label>
      <input
        id="lexical-image-url"
        className={fieldClasses}
        value={url}
        placeholder="https://"
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (!url.trim()) return;
          insert(url.trim());
          close();
        }}
      />
      <button
        type="button"
        className={primaryButtonClasses}
        disabled={!url.trim()}
        onClick={() => {
          insert(url.trim());
          close();
        }}
      >
        {t('editor.toolbar.insert')}
      </button>
    </div>
  );
};
